import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Actions, ofActionDispatched, Select } from '@ngxs/store';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { FetchCourseOfferingParticipantsSuccess, FetchProgramExecutionCourseOfferingParticipantsSuccess } from 'app/library/course-offering-participants/actions';
import { CourseOfferingParticipantsState } from 'app/library/course-offering-participants/models/course-offering-participants.state';
import { CourseOfferingParticipantsService } from 'app/library/course-offering-participants/services/course-offering-participants.service';
import { IModuleUserDateRangeFilters } from 'app/library/module-user/interfaces/module-user-date-range-filters.interface';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

export class CourseOfferingParticipantsDataSource implements DataSource<ModuleUser> {
    @Select(CourseOfferingParticipantsState.getFiltersList)
    filter$: Observable<ModuleUser>;

    private _loadingSubject = new BehaviorSubject<boolean>(false);

    public loading$ = this._loadingSubject.asObservable();

    constructor(private _filteredUser$: Observable<ModuleUser[]>, private _action$: Actions, private _courseOfferingParticipantsService: CourseOfferingParticipantsService) {}

    connect(collectionViewer: CollectionViewer): Observable<ModuleUser[]> {
        return this._filteredUser$;
    }

    disconnect(collectionViewer: CollectionViewer): void {
        this._loadingSubject.complete();
    }

    async loadCourseOfferingParticipants(
        courseOfferingId: string,
        programExecutionId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize?: number,
        pageNumber?: number,
        withoutCertificate?: boolean,
        selectedCertificateIds?: string[],
        selectedStatusIds?: string[],
        selectedExecutionIds?: string[],
        dateRangeFilters?: IModuleUserDateRangeFilters,
        audienceIds?: string[],
        teamIds?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        isUnassignedSelectedAudiences?: boolean,
        isUnassignedSelectedTeams?: boolean,
        excludedTeamIds?: string[],
        teamRoleIds?: string[],
        columns?: string[]
    ): Promise<void> {
        this._loadingSubject.next(true);

        if (programExecutionId) {
            // retrieves data based on relevant executions and program participants
            await this._courseOfferingParticipantsService.fetchProgramExecutionParticipantsByCourseOfferingId(
                courseOfferingId,
                programExecutionId,
                search,
                sortBy,
                sortOrder,
                pageSize,
                pageNumber,
                withoutCertificate,
                selectedCertificateIds,
                selectedStatusIds,
                selectedExecutionIds,
                dateRangeFilters,
                audienceIds,
                teamIds,
                extendedAttributesFilters,
                isUnassignedSelectedAudiences,
                isUnassignedSelectedTeams,
                excludedTeamIds,
                teamRoleIds,
                columns
            );
        } else {
            // accessible only by the tenant admin and retrieves all data
            await this._courseOfferingParticipantsService.fetchParticipantsByCourseOfferingId(
                courseOfferingId,
                search,
                sortBy,
                sortOrder,
                pageSize,
                pageNumber,
                withoutCertificate,
                selectedCertificateIds,
                selectedStatusIds,
                selectedExecutionIds,
                dateRangeFilters,
                audienceIds,
                teamIds,
                extendedAttributesFilters,
                isUnassignedSelectedAudiences,
                isUnassignedSelectedTeams,
                excludedTeamIds,
                teamRoleIds,
                columns
            );
        }

        this._action$
            .pipe(
                ofActionDispatched(FetchCourseOfferingParticipantsSuccess, FetchProgramExecutionCourseOfferingParticipantsSuccess),
                finalize(() => this._loadingSubject.next(false)),
                take(1)
            )
            .subscribe();
    }
}
